import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.less";
import { MessageSendType } from "@/types/chat/enum";
import {
  buildReplyEmojiMessageBody,
  convertEmployeeToString,
  isCardViewGroupSession,
  isEqualEmployee,
  QUICK_REPLY_TIP_KEY,
  sendReplyEmojiMessage,
  sleep
} from "@/utils/chat/message";
import ChatState from "@/types/chat/State";
import { connect } from "dva";
import { getClientType } from "@/utils";
import { ClientType } from "@/types/chat";
import ImService from "@/server/ImService";
import Bus from "@/utils/bus";
import ChatContext from "@/components/chat/context";
import Text from "@/components/chat/message/Text";
import { getTextMessageContent } from "@/components/chat/utils/commonMessageModal";
import { compareUserProp, compareProp } from "@/utils/chat/index";
import { message as toast, Tooltip } from "antd";
import { MessageExtAction } from "@jd/jdee.im.sdk/lib/es/protocol/messageExtend/Type";
import ReplyEmojiDetail from "./ReplyEmojiDetail";
import ReplyMessage from "@/components/chat/message/ReplyMessage";
import { UserCardType } from "@/types/UserCard";
import { convertUIDToString } from "@jd/focus-desktop-comps/lib/es/utils";
import IconFont from "@/components/icon";

/**
 * 常用语回复
 * @returns
 */
function ReplyEmojiCard(props: any) {
  const { lastMessage } = React.useContext(ChatContext);
  const {
    sendType,
    message,
    selectedSession,
    currentEmployee,
    notShowReply,
    quickReplys,
    children
  } = props;
  const [replyDetail, setReplyDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isOthers = useMemo(() => {
    return sendType === MessageSendType.OTHER;
  }, [sendType]);

  // 回复
  const handleQuickReply = useCallback((item: any) => {
      // 发送快速回复
      setLoading(true);
      const hasReply = item.users.find(
        (i: any) => convertEmployeeToString(currentEmployee) === convertEmployeeToString(i)
      );
      const msgContentItem = {
        text: item.text,
        emojiCode: item.emojiCode,
        users: [
          {
            pin: currentEmployee.userId,
            app: currentEmployee.app,
            teamId: currentEmployee.teamId,
            clientType: getClientType() === "MAC" ? ClientType.MAC : ClientType.PC,
            timeStamp: new Date().getTime()
          }
        ]
      };
      sendReplyEmojiMessage({
        action: hasReply ? MessageExtAction.DELETE : MessageExtAction.ADD,
        session: selectedSession,
        message,
        msgContentItem
      });
    },
    [currentEmployee, selectedSession, message]
  );

  // 撤回回复
  const handleRevokeReply = useCallback(() => {
    const msg = buildReplyEmojiMessageBody({
      mid: message.mid,
      session: selectedSession,
      msgContentItem: {
        users: [
          {
            pin: currentEmployee.userId,
            app: currentEmployee.app,
            teamId: currentEmployee.teamId,
            clientType: getClientType() === "MAC" ? ClientType.MAC : ClientType.PC,
            timeStamp: new Date().getTime()
          }
        ]
      },
      action: MessageExtAction.DELETE
    });
    if (msg) {
      ImService.getInstance()
        ?.sendChatMessageExt(selectedSession.sessionId, msg)
        .finally(() => {
          toast.success({
            key: QUICK_REPLY_TIP_KEY,
            content: "已撤回回复"
          });
          setLoading(false);
        });
    }
  }, [
    currentEmployee.app,
    currentEmployee.teamId,
    currentEmployee.userId,
    message.mid,
    selectedSession
  ]);

  const openUserCard = useCallback(
    (user: any) => {
      if (isCardViewGroupSession(selectedSession)) {
        Bus.emit("app:toggle-user-card-modal:show", {
          visible: true,
          type: UserCardType.User,
          userId: user.userId,
          teamId: user.teamId,
          appId: user.app
        });
      } else {
        toast.warn({
          key: QUICK_REPLY_TIP_KEY,
          content: "群创建人已关闭群成员查看其他人名片页"
        });
      }
    },
    [selectedSession]
  );

  // 排序
  const replyUsers = useMemo(() => {
    if (!message?.msgContent?.length) {
      return [];
    }
    const list = message.msgContent.map((i: any) => {
      const users = (i.users || []).sort(compareUserProp("timeStamp", currentEmployee));
      return {
        ...i,
        users
      };
    });
    return list;
  }, [currentEmployee, message.msgContent]);

  const replyUserTimes = useMemo(() => {
    if (!message?.msgContent?.length) {
      return [];
    }
    const list = message.msgContent.map((i: any) => {
      const users = (i.users || []).sort(compareProp("timeStamp"));
      return {
        ...i,
        users
      };
    });
    return list;
  }, [message.msgContent]);

  const hasReply = useMemo(() => {
    return message?.msgContent?.length;
  }, [message]);

  const handleOpenDetail = useCallback((item: any) => {
    setReplyDetail({
      visible: true,
      currSelected: item.emojiCode
    });
  }, []);

  const getStyle = useCallback(
    (code: string, text: string) => {
      const item = quickReplys.find((i: any) => i.id === code || i.detail === text);
      return {
        background: item?.backgroundColor || "",
        borderColor: item?.frameColor || "",
        color: item?.textColor || "",
        "--color": item?.hoverColor || ""
      };
    },
    [quickReplys]
  );

  const replyComp = useMemo(() => {
    return (
      <div className={styles["reply-wrap"]}>
        {replyUsers?.length
          ? replyUsers.map((item: any, index: number) => {
              item.users = item.users || [];
              const len = item.users.length;
              const list = item.users.slice(0, 3);
              const usersName = list.map((a: any, idx: number) => {
                return (
                  <Tooltip title="点击查看个人名片页" key={convertEmployeeToString(a)}>
                    <span onClick={() => openUserCard(a)}>
                      {a.name}
                      {idx < list.length - 1 ? "、" : ""}
                    </span>
                  </Tooltip>
                );
              }, "");
              const style = getStyle(item.emojiCode, item.text);
              const hasReply = item.users.find(
                (i: any) => convertEmployeeToString(currentEmployee) === convertEmployeeToString(i)
              );
              return (
                <div
                  className={styles["item"]}
                  key={`reply_${message.id}_${item.emojiCode}_${index}`}
                >
                  <div
                    className={styles["tag"]}
                    onClick={() => handleQuickReply(item)}
                    style={style}
                  >
                    <Tooltip title={hasReply ? "点击撤回" : "点击回复"}>{item.text}</Tooltip>
                  </div>
                  <span className={`${styles["name"]} ellipsis`}>{usersName}</span>
                  {len > 3 ? (
                    <Tooltip title="点击查看统计详情">
                      <span
                        className={styles["text"]}
                        onClick={() => handleOpenDetail(item)}
                      >{`+${len}人`}</span>
                    </Tooltip>
                  ) : null}
                </div>
              );
            })
          : null}
      </div>
    );
  }, [
    currentEmployee,
    getStyle,
    handleOpenDetail,
    handleQuickReply,
    message.id,
    openUserCard,
    replyUsers
  ]);

  const isSelf = useMemo(() => {
    return isEqualEmployee(message.sender, currentEmployee);
  }, [currentEmployee, message?.sender]);

  useEffect(() => {
    if (lastMessage?.id === message.id && !isOthers) {
      sleep(60).then(() => {
        wrapRef?.current?.scrollIntoView({
          block: "end"
        });
      });
    }
  }, [isOthers, lastMessage.id, message.id]);
  return !hasReply ? (
    <Text {...props} />
  ) : (
    <>
      <div
        ref={wrapRef}
        className={`${styles["reply-emoji-card-wrap"]} ${
          !isOthers ? styles["owner"] : styles["other"]
        }`}
      >
        <div className={styles["content"]}>
          {children}
          {hasReply && replyComp}
        </div>
        {!notShowReply && selectedSession.isGroup && message?.tag === 1 && (
          <IconFont
            className={`${styles["tag-icon"]} ${isSelf ? styles["mine"] : styles["others"]}`}
            type={!isSelf ? "iconic_app_mark_solid_left" : "iconic_app_mark_solid_right"}
          />
        )}
      </div>
      {replyDetail && (
        <ReplyEmojiDetail
          detailVisible={replyDetail?.visible}
          handleClose={() => setReplyDetail(null)}
          replyUsers={replyUserTimes}
          hasReply={hasReply}
          isOwner={!isOthers}
          handleRevokeReply={handleRevokeReply}
          openUserCard={openUserCard}
          currSelected={replyDetail?.currSelected}
        />
      )}
    </>
  );
}

function mapStateToProps({ chat }: { chat: ChatState }) {
  const { selectedSession, currentEmployee, quickReplys } = chat;
  return {
    selectedSession,
    currentEmployee,
    quickReplys

  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSessionMessageMid(data: { sendResult: any; sessionId: string }) {
      dispatch({ type: "chat/updateSessionMessageMid", payload: data });
    }
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(ReplyEmojiCard);
